import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"

interface PausedIdentityProps {
  name: string
  brandName: string
  /** Optional avatar URL; `null` on current main → initials fallback. */
  avatarUrl?: string | null
}

/**
 * OKS-404 — Greyed identity block (UX Spec §2.2).
 *
 * Visual de-emphasis ONLY: the avatar renders at 0.55 opacity and the
 * name in a muted weight, signalling "paused" without hiding information.
 * The name text stays readable for assistive tech (de-emphasis is visual,
 * not semantic). Missing avatar → initials fallback, still at 0.55 opacity.
 *
 * Self-contained for OKS-404 (initials-only): it does NOT depend on
 * OKS-387's unmerged `users.profile_image_url`. `avatarUrl` is wired
 * through for forward-compat — when an avatar source lands, callers can
 * pass it and the image branch renders automatically (Radix falls back to
 * the initials when the src is empty or fails to load).
 */
export function PausedIdentity({
  name,
  brandName,
  avatarUrl = null,
}: PausedIdentityProps) {
  const { t } = useTranslation()
  const getInitials = useInitials()

  return (
    <div
      className="flex flex-col items-center gap-2 text-center"
      data-testid="paused-identity"
    >
      <Avatar className="size-20 opacity-55" data-testid="paused-avatar">
        {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
        <AvatarFallback className="text-lg">{getInitials(name)}</AvatarFallback>
      </Avatar>

      <p
        className="text-muted-foreground max-w-full truncate text-lg font-medium"
        data-testid="paused-name"
      >
        {name}
      </p>

      <p
        className="text-muted-foreground line-clamp-2 text-sm"
        data-testid="paused-subline"
      >
        {t("crew_paused.subline", { brand: brandName })}
      </p>
    </div>
  )
}
